import { Status } from 'hooks/requests/fetchUtils';
import useFetchApi from 'hooks/useFetchApi';
import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useFmcHost from 'hooks/useFmcHost';
import { pickBy } from 'ramda';
import isPresent from 'utils/isPresent';

import useQueryParameters from 'hooks/useQueryParameters';

import { CreditApplication, lockFields } from 'state/creditApplication';
import { mergePrequalOffer } from 'state/prequalOffer';
import { mergeApplication } from 'state/creditApplication';
import { FinanceResult, setFinanceOffers } from 'state/financeOffers';
import { setPrescreenId } from 'state/prescreen';

import { selectTenant } from 'state/tenant';
import useIovation from 'hooks/useIovation';
import { unifiedOffersReceived } from 'fmc-sdk';
import useAnalyticsVariables from 'hooks/useAnalyticVariables';
import { transformDob, transformDobFromApi } from 'utils/dateUtils';
import { PrequalResult } from './prequalification';
import useHandleExistingAccount from './handleExistingAccount';
import useApplicationBasedOverrides from 'hooks/useApplicationBasedOverrides';

const PATH = '/api/unified_offer';

type UnifiedOffersHook = () => [
  (params: CreditApplication) => Promise<UnifiedOffersResult>,
  Status,
  boolean,
];

export interface PartialError {
  error: true;
  code?: string;
  message?: string;
}

export interface UnifiedOffersResult {
  financing?: FinanceResult | PartialError;
  prequalification?: PrequalResult | PartialError;
}

export class UnifiedOffersError extends Error {}

const useUnifiedOffers: UnifiedOffersHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();
  const {
    mockUO,
    storeNumber: queryStoreNumber,
    location,
    channel,
    subchannel,
    subId,
    financingLocationId,
  } = useQueryParameters();

  const dispatch = useDispatch();
  const fmcDispatch = useFmcHost();
  const {
    setAnalyticsVariablesByApplication,
    setAnalyticsVariables,
  } = useAnalyticsVariables();
  const handleExistingAccount = useHandleExistingAccount();

  const { defaultStoreNumber } = useSelector(selectTenant);
  const overridesAttributes = useApplicationBasedOverrides();

  const iovationBlackbox = useIovation();

  const unifiedOffersRequest = useCallback(
    async (params: CreditApplication): Promise<UnifiedOffersResult> => {
      setError(false);

      const blackbox = await iovationBlackbox;
      const overrides: Partial<CreditApplication> = {
        storeNumber:
          params.storeNumber || queryStoreNumber || defaultStoreNumber,
      };

      params = transformDob(['dateOfBirth'], params);

      const response = await fetch(PATH, {
        method: 'POST',
        body: {
          ...overrides,
          ...params,
          ...overridesAttributes,
          creditKarmaId: subId,
          iovationBlackbox: blackbox,
          mockResponse: mockUO,
          location,
          channel,
          subchannel,
          financingLocationId,
        },
      });

      setAnalyticsVariablesByApplication(params);

      if (response.ok) {
        const result: UnifiedOffersResult = await response.json();
        let fmcDispatchPrequalificationId;
        let fmcDispatchHasExistingAccount;
        let fmcDispatchfinancingBuyerId;
        let fmcDispatchPrequalCreditLimit;

        // Handle financing
        const financing = result.financing;
        if (financing) {
          if ('error' in financing) {
            // Send result to SDK
          } else {
            if (financing.status === 'APPROVED') {
              fmcDispatchfinancingBuyerId = financing.buyerId;
              setAnalyticsVariables({
                breadBuyerId: financing.buyerId,
              });
            }
            dispatch(setFinanceOffers(financing));
          }
        }

        // Handle prequalOffer
        let prequalOffer = result.prequalification;
        if (prequalOffer) {
          if ('error' in prequalOffer) {
            // Send result to SDK
          } else {
            prequalOffer = transformDobFromApi(['dateOfBirth'], prequalOffer);
            setAnalyticsVariables({
              applicationId: prequalOffer.applicationId,
              accountId: prequalOffer.accountId,
            });

            if (prequalOffer.hasExistingAccount) {
              fmcDispatchHasExistingAccount = prequalOffer.hasExistingAccount;
              handleExistingAccount(
                prequalOffer,
                {
                  sendToSdk: true,
                },
                {
                  mobilePhone:
                    params.mobilePhone || params.alternativePhone || null,
                },
              );
              dispatch(mergePrequalOffer(prequalOffer));
            } else if (
              (prequalOffer.prequalificationId && prequalOffer.hasOffer) ||
              prequalOffer.notQualified
            ) {
              if (!prequalOffer.notQualified) {
                // Should not set these in the notQualified case
                fmcDispatchPrequalificationId = prequalOffer.prequalificationId;
                fmcDispatchPrequalCreditLimit = prequalOffer.creditLimit;
                setAnalyticsVariables({
                  prequalificationId: prequalOffer.prequalificationId,
                });
              }

              dispatch(mergePrequalOffer(prequalOffer));
            } else if (prequalOffer.prescreenId && prequalOffer.hasOffer) {
              dispatch(setPrescreenId(prequalOffer.prescreenId));
            }
          }
        }

        dispatch(mergeApplication(pickBy(v => isPresent(v), prequalOffer)));

        if (
          fmcDispatchfinancingBuyerId ||
          fmcDispatchHasExistingAccount ||
          fmcDispatchPrequalificationId ||
          fmcDispatchPrequalCreditLimit ||
          overridesAttributes.selectedCardKey
        ) {
          fmcDispatch(
            unifiedOffersReceived({
              financingBuyerId: fmcDispatchfinancingBuyerId,
              hasExistingAccount: fmcDispatchHasExistingAccount,
              prequalificationId: fmcDispatchPrequalificationId,
              prequalCreditLimit: fmcDispatchPrequalCreditLimit,
              selectedCardKey: overridesAttributes.selectedCardKey,
            }),
          );
        }
        // Lock selected card key if present
        overridesAttributes.selectedCardKey &&
          dispatch(
            lockFields({
              selectedCardKey: true,
            }),
          );
        return Promise.resolve(result);
      } else {
        setError(true);
        return Promise.reject(new UnifiedOffersError());
      }
    },
    [
      iovationBlackbox,
      queryStoreNumber,
      defaultStoreNumber,
      fetch,
      overridesAttributes,
      subId,
      mockUO,
      location,
      channel,
      subchannel,
      setAnalyticsVariablesByApplication,
      dispatch,
      setAnalyticsVariables,
      handleExistingAccount,
      fmcDispatch,
      financingLocationId,
    ],
  );

  return [unifiedOffersRequest, status, error];
};

export default useUnifiedOffers;
